import { createViewer } from './viewer-controller.js';
import type { AssemblyManifest, LoadedNode, ViewerController } from './viewer-controller.js';
import { ExplosionGraphExplode } from './explode/explode-strategy.js';
import { mountRegroupPanel } from './regroup-panel.js';
import { mountVariantB, type PartData } from './overlay/variant-b.js';
import { createViewerBus } from './events.js';
import { ErpClient, readErpApiBaseFromDom, type ErpProduct } from '@idiagram/erp-client';
import { startLiveUpdates } from './live-updates.js';

// Mirror of converter's bom-types.ts — kept here so the viewer has no
// dependency on the converter package.
interface BomRow {
  index: number;
  partNumber: string;
  name: string;
  qty: number;
  parentAssembly?: string;
}

interface BomLink {
  rowIndex: number;
  nodeIds: string[];
  confidence: number;
  reason: string;
}

interface BomAmbiguous {
  rowIndex: number;
  candidates: Array<{ nodeId: string; confidence: number; reason: string }>;
}

interface BomLinksFile {
  schemaVersion: '1';
  rows: BomRow[];
  links: BomLink[];
  unmatched: number[];
  ambiguous: BomAmbiguous[];
  stats: { rowCount: number; matched: number; ambiguous: number; unmatched: number; matchRate: number };
}

async function main(): Promise<void> {
  const container = document.getElementById('canvas-container');
  const status = document.getElementById('status');
  const treePanel = document.getElementById('tree-panel');
  const bomPanel = document.getElementById('bom-panel');
  const regroupPanelEl = document.getElementById('regroup-panel');
  const regroupToggle = document.getElementById('regroup-toggle') as HTMLButtonElement | null;
  if (!container || !status || !treePanel || !bomPanel || !regroupPanelEl || !regroupToggle) {
    throw new Error('viewer DOM missing');
  }

  const params = new URLSearchParams(window.location.search);
  // Assembly key precedence:
  //   1. ?assembly=... query param (legacy + intake-service's post-redirect form)
  //   2. URL pathname (eg the bare /embed/<org>/<slug> form on demo.quidities.com)
  //   3. as1-oc-214 fallback (only fires when neither is present — e.g. root /)
  // The whole pathname is taken as the key so /{key}/manifest.json resolves
  // to a real artefact under either host's content layout (intake serves
  // /embed/<org>/<slug>/manifest.json; demo's nginx 302s to ?assembly=<slug>
  // first, so this branch never fires on the flat demo layout).
  const pathKey = window.location.pathname.replace(/^\/+|\/+$/g, '');
  const assembly = params.get('assembly') ?? (pathKey ? pathKey : 'as1-oc-214');
  const lang = params.get('lang') ?? undefined;
  // Embed mode: 'storefront' suppresses the sidebar (tree/BOM/regroup/live)
  // and the in-viewer cart pill so the host owns the cart UI. Bus events are
  // bridged out via window.parent.postMessage. Use this on quidities.com hero,
  // Shopify/Odoo storefront embeds, and anywhere the viewer is a sub-widget
  // rather than the whole page. The default (no param) keeps the full
  // standalone working-tool UX.
  const embedMode = params.get('embed');
  const embedStorefront = embedMode === 'storefront';
  if (embedStorefront) {
    document.body.classList.add('embed-storefront');
  }
  const manifestUrl = `/${assembly}/manifest.json`;
  const bomLinksUrl = `/${assembly}/bom-links.json`;
  const erpApiBase = readErpApiBaseFromDom();
  const erpClient = erpApiBase ? new ErpClient({ baseUrl: erpApiBase }) : null;

  status.textContent = `loading ${assembly}…`;

  // Peek at the manifest before creating the viewer so we can pick the explode
  // strategy: when the converter emitted a blocking-aware `explosionGraph`
  // (idiagram#3), default to ExplosionGraphExplode; otherwise fall back to the
  // v0 RadialHierarchyExplode (the renderer's default when no strategy is
  // injected). loadAssembly re-fetches the manifest; this extra fetch is a tiny
  // cached JSON and keeps the strategy decision out of the renderer.
  let preManifest: AssemblyManifest | null = null;
  try {
    const resp = await fetch(manifestUrl);
    if (resp.ok) preManifest = (await resp.json()) as AssemblyManifest;
  } catch {
    /* loadAssembly will surface the real fetch error below */
  }
  const explosionGraph = preManifest?.explosionGraph;
  const explodeStrategy =
    explosionGraph && explosionGraph.nodes.length > 0
      ? new ExplosionGraphExplode(explosionGraph.nodes)
      : undefined;
  console.info(
    `[viewer] explode strategy: ${explodeStrategy ? explodeStrategy.name : 'radial-hierarchy (no explosion graph in manifest)'}`,
  );

  let viewer: ViewerController;
  try {
    viewer = await createViewer({ container, explodeStrategy });
  } catch (err) {
    status.textContent = `viewer init failed: ${err instanceof Error ? err.message : String(err)}`;
    console.error(err);
    return;
  }

  performance.mark('idiagram-load-start');
  try {
    await viewer.loadAssembly(manifestUrl);
  } catch (err) {
    status.textContent = `failed to load ${assembly}: ${err instanceof Error ? err.message : String(err)}`;
    console.error(err);
    return;
  }
  // Wait one rAF tick so the scene is actually rendered to the canvas before
  // we stamp the "first-frame" mark.
  await new Promise<void>((res) => requestAnimationFrame(() => res()));
  performance.mark('idiagram-first-frame');
  performance.measure('idiagram-load-to-first-frame', 'idiagram-load-start', 'idiagram-first-frame');

  // M6 derisking — placeholder; the variant-B overlay below subsumes the
  // bare-dot test once we have the BOM/ERP lookups built.

  const manifest = viewer.getManifest();
  let statusBase = '';
  if (manifest) {
    statusBase =
      `${assembly} — ${manifest.geometry.nodeCount} nodes, ` +
      `${manifest.geometry.meshCount} meshes, ` +
      `${manifest.geometry.triangleCount.toLocaleString()} tris ` +
      `(${manifest.kernel.name})`;
    status.textContent = statusBase;
  }

  // Try to load the BOM-links sidecar (optional — only present when the
  // converter was run with --bom).
  let bomLinks: BomLinksFile | null = null;
  try {
    const resp = await fetch(bomLinksUrl);
    if (resp.ok) bomLinks = (await resp.json()) as BomLinksFile;
  } catch {
    /* ignore */
  }

  if (bomLinks) {
    status.textContent =
      `${statusBase} | BOM: ` +
      `${bomLinks.stats.matched}/${bomLinks.stats.rowCount} matched ` +
      `(${Math.round(bomLinks.stats.matchRate * 100)}%), ` +
      `${bomLinks.stats.ambiguous} ambiguous, ${bomLinks.stats.unmatched} unmatched`;
  }

  // When <meta name="erp-api"> points at an integration service, fetch the
  // ERP overlay and build a lookup keyed by SKU. The viewer falls back to
  // the static BOM view if the call fails or the meta tag is empty.
  const erpBySku = new Map<string, ErpProduct>();
  let erpPublicUrl: string | null = null;
  let erpEventsUrl: string | null = null;
  if (erpClient) {
    try {
      const erpManifest = await erpClient.getErpManifest(assembly, lang);
      for (const n of erpManifest.nodes) {
        if (n.productRef && n.product) erpBySku.set(n.productRef, n.product);
      }
      erpPublicUrl = erpManifest.erp.publicUrl;
      erpEventsUrl = erpManifest.erp.eventsUrl;
      if (erpBySku.size > 0) {
        status.textContent =
          `${status.textContent} | ERP: ${erpBySku.size} live, lang=${erpManifest.erp.lang}`;
      }
    } catch (err) {
      console.warn('[viewer] erp-manifest fetch failed; falling back to static view', err);
    }
  }

  // Static-ERP fallback: when there's no live integration-service (e.g. the
  // public demo build, or any deploy without a wired ERP backend), look for
  // an /{assembly}/erp.json sidecar with the same ErpProduct shape. Lets the
  // demo cart/price/stock UX feel alive without standing up Odoo behind it.
  if (erpBySku.size === 0) {
    try {
      const resp = await fetch(`/${assembly}/erp.json`);
      if (resp.ok) {
        const data = (await resp.json()) as { products?: ErpProduct[] };
        for (const p of data.products ?? []) erpBySku.set(p.sku, p);
        if (erpBySku.size > 0) {
          status.textContent = `${status.textContent} | ERP: ${erpBySku.size} static`;
        }
      }
    } catch {
      /* no static erp; viewer renders without prices */
    }
  }

  // Build the two-way index between BOM rows and node ids.
  const nodeIdToRowIndexes = new Map<string, number[]>();
  const rowIndexToNodeIds = new Map<number, string[]>();
  const bomRowByIndex = new Map<number, BomRow>();
  if (bomLinks) {
    for (const row of bomLinks.rows) bomRowByIndex.set(row.index, row);
    for (const link of bomLinks.links) {
      rowIndexToNodeIds.set(link.rowIndex, link.nodeIds);
      for (const nid of link.nodeIds) {
        const arr = nodeIdToRowIndexes.get(nid) ?? [];
        arr.push(link.rowIndex);
        nodeIdToRowIndexes.set(nid, arr);
      }
    }
  }

  // M6 overlay: mount the variant-B interaction layer over the canvas. Data
  // flows out from BOM rows + ERP overlay (built above) via getPartData; the
  // viewer itself stays data-agnostic. The bus is the public event surface —
  // a host page (homepage embed, customer storefront wrapper) subscribes to
  // these to do the actual cart/checkout behaviour.
  const viewerBus = createViewerBus();
  const variantB = mountVariantB({
    container,
    viewer,
    bus: viewerBus,
    getPartData: (id) => buildPartData(id, viewer.getNodes(), nodeIdToRowIndexes, bomRowByIndex, erpBySku),
    showCart: !embedStorefront,
  });
  // Derisking-grade host: log cart/checkout events so we can see end-to-end
  // wiring in the smoke test. Real hosts will Shopify.addItem / call Odoo / …
  viewerBus.on('cart:add', (item) => {
    console.info('[viewer-bus] cart:add', item);
  });
  viewerBus.on('cart:open', () => console.info('[viewer-bus] cart:open'));
  viewerBus.on('checkout:request', (p) => console.info('[viewer-bus] checkout:request', p));
  // Storefront embed: bridge the public bus events out to the host page via
  // postMessage so a parent frame can wire its own cart/checkout UI. We send
  // to '*' because the host's origin isn't known at build time; the host
  // filters by `source === 'idiagram-viewer'` and the iframe sandbox attribute
  // (allow-same-origin only) bounds who can mount us in the first place.
  if (embedStorefront && window.parent !== window) {
    const post = (type: string, payload: unknown): void => {
      window.parent.postMessage({ source: 'idiagram-viewer', assembly, type, payload }, '*');
    };
    viewerBus.on('cart:add', (item) => post('cart:add', item));
    viewerBus.on('cart:remove', (p) => post('cart:remove', p));
    viewerBus.on('cart:open', () => post('cart:open', {}));
    viewerBus.on('checkout:request', (p) => post('checkout:request', p));
    viewerBus.on('part:select', (p) => post('part:select', { id: p.id, prev: p.prev }));
    viewerBus.on('part:deselect', (p) => post('part:deselect', p));
    // Signal readiness so the host can stop showing a skeleton / loading state.
    post('ready', { manifestNodes: manifest?.geometry.nodeCount ?? 0 });
  }
  // Expose the variant-B handle + bus for the M6 smoke / browser console.
  (window as unknown as { __idiagramVariantB?: unknown; __idiagramBus?: unknown }).__idiagramVariantB = variantB;
  (window as unknown as { __idiagramVariantB?: unknown; __idiagramBus?: unknown }).__idiagramBus = viewerBus;

  // Explode slider — wired in BOTH modes. The slider DOM stays visible in
  // storefront embed (it's a core "play with the model" affordance), so its
  // input handler has to be registered before the embed-mode early return.
  const slider = document.getElementById('explode-slider') as HTMLInputElement | null;
  const explodeValue = document.getElementById('explode-value');
  slider?.addEventListener('input', () => {
    const factor = Number(slider.value) / 100;
    viewer.explode(factor);
    viewerBus.emit('view:explode', { factor });
    if (explodeValue) explodeValue.textContent = `${slider.value}%`;
  });

  // Fit-to-target + targeted reveal — wired in BOTH modes (the embed is a
  // part-picker, so clicking a dot there must still fly + focus; registered
  // before the embed early-return). The variant-B overlay turns a dot/click into
  // a part:select / part:deselect on the bus; we translate those into the
  // ViewerController behaviour.
  //
  // When the manifest carries the v2 explosion-graph blocker data, selecting a
  // part REVEALS it (idiagram#6): the parts covering it from the current camera
  // slide out of the way, the target + movers stay bright while the rest dims,
  // and the camera eases onto the target. Without that data we keep the v1
  // behaviour — highlight + isolate (dim the rest) + frame. selectNodes() always
  // adds the emissive highlight. No cart UI is touched here; the embed/host cart
  // split is unchanged.
  viewerBus.on('part:select', ({ id }) => {
    viewer.selectNodes([id]);
    if (viewer.supportsReveal()) {
      // revealNode encapsulates move-occluders + isolate([target, …movers]) + frame.
      viewer.revealNode(id);
    } else {
      viewer.isolateNodes([id]);
      viewer.frameNodes([id]);
    }
  });
  viewerBus.on('part:deselect', () => {
    viewer.clearSelection();
    if (viewer.supportsReveal()) viewer.clearReveal();
    else viewer.frameNodes([]); // ease back to the whole-assembly fit
  });

  // Storefront embed: the sidebar (tree / BOM / regroup / live-updates) and
  // its 3D-pick selection wiring belong to the standalone working tool, not
  // an embed. Skip the rest — the host page owns the surrounding chrome. The
  // body class set above hides the sidebar via index.html CSS.
  if (embedStorefront) {
    return;
  }

  // Tree panel — only "real" assembly nodes, no OCCT outer wrapper.
  const rootId = viewer.getRootId();
  const nodes = viewer.getNodes();
  treePanel.innerHTML = '';
  if (rootId) {
    const realRootId = findFirstRealNode(rootId, nodes);
    if (realRootId) {
      const ul = document.createElement('ul');
      ul.className = 'tree-root';
      ul.appendChild(buildTreeItem(nodes, realRootId, nodeIdToRowIndexes));
      treePanel.appendChild(ul);
    }
  }

  // BOM panel.
  bomPanel.innerHTML = '';
  if (bomLinks) {
    bomPanel.appendChild(buildBomTable(bomLinks, erpBySku, erpPublicUrl));
  } else {
    bomPanel.innerHTML = '<em style="color:#888;">No BOM linked. Run `pnpm convert ... --bom <csv>`.</em>';
  }

  // Wire two-way linking.
  const treeItems = new Map<string, HTMLElement>();
  treePanel.querySelectorAll<HTMLElement>('[data-idiagram-id]').forEach((el) => {
    const id = el.dataset.idiagramId;
    if (id) treeItems.set(id, el);
  });

  const bomRowEls = new Map<number, HTMLElement>();
  bomPanel.querySelectorAll<HTMLElement>('[data-row-index]').forEach((el) => {
    const idx = Number(el.dataset.rowIndex);
    if (!Number.isNaN(idx)) bomRowEls.set(idx, el);
  });

  function setSelection(nodeIds: string[], rowIndexes: number[], frame = false): void {
    viewer.selectNodes(nodeIds);
    if (frame && nodeIds.length > 0) {
      // Same fit-to-target treatment as a dot click: ease the camera onto the
      // selection and isolate it. A bare tree-row click (frame=false) only
      // highlights, leaving the camera + the rest of the model alone.
      viewer.isolateNodes(nodeIds);
      viewer.frameNodes(nodeIds);
    } else if (nodeIds.length === 0) {
      viewer.isolateNodes([]);
    }
    for (const [id, el] of treeItems) el.classList.toggle('selected', nodeIds.includes(id));
    for (const [idx, el] of bomRowEls) el.classList.toggle('selected', rowIndexes.includes(idx));
  }

  // Regroup state. When active, the 3D pick handler talks to the regroup panel
  // (leaf-id) instead of the BOM linker (real-id).
  // Re-bind to locals so TS narrows through closures.
  const regroupToggleEl: HTMLButtonElement = regroupToggle;
  const regroupBody: HTMLElement = regroupPanelEl;
  const bomBody: HTMLElement = bomPanel;
  const treeBody: HTMLElement = treePanel;

  let regroupActive = false;
  let regroupHandle: ReturnType<typeof mountRegroupPanel> | null = null;
  const leafNodes = Array.from(viewer.getNodes().values()).filter((n) => n.synthetic);

  function enterRegroup(): void {
    regroupActive = true;
    regroupToggleEl.classList.add('active');
    regroupToggleEl.textContent = 'Exit regroup';
    regroupBody.style.display = 'block';
    bomBody.style.display = 'none';
    treeBody.style.display = 'none';
    viewer.clearSelection();
    regroupHandle = mountRegroupPanel({
      container: regroupBody,
      viewer,
      leafNodes,
      assemblyName: assembly,
    });
  }
  function exitRegroup(): void {
    regroupActive = false;
    regroupToggleEl.classList.remove('active');
    regroupToggleEl.textContent = 'Regroup mode';
    regroupBody.style.display = 'none';
    bomBody.style.display = 'block';
    treeBody.style.display = 'block';
    viewer.clearSelection();
    regroupHandle?.dispose();
    regroupHandle = null;
  }
  regroupToggleEl.addEventListener('click', () => {
    if (regroupActive) exitRegroup();
    else enterRegroup();
  });

  // Click in 3D → highlight that node + its BOM rows OR (in regroup mode)
  // toggle the leaf into the regroup selection.
  viewer.onPick((id, leafId) => {
    if (regroupActive) {
      regroupHandle?.applyPickedLeaf(leafId);
      return;
    }
    if (!id) {
      setSelection([], []);
      return;
    }
    const linkedRows = nodeIdToRowIndexes.get(id) ?? [];
    setSelection([id], linkedRows);
  });

  // Click a tree row → highlight the node + its BOM rows.
  treePanel.addEventListener('click', (ev) => {
    const li = (ev.target as HTMLElement).closest<HTMLElement>('[data-idiagram-id]');
    if (!li) return;
    const id = li.dataset.idiagramId;
    if (!id) return;
    const linkedRows = nodeIdToRowIndexes.get(id) ?? [];
    setSelection([id], linkedRows);
  });

  // Click a BOM row → highlight + frame the linked nodes.
  bomPanel.addEventListener('click', (ev) => {
    const tr = (ev.target as HTMLElement).closest<HTMLElement>('[data-row-index]');
    if (!tr) return;
    const idx = Number(tr.dataset.rowIndex);
    if (Number.isNaN(idx)) return;
    const linkedNodes = rowIndexToNodeIds.get(idx) ?? [];
    setSelection(linkedNodes, [idx], true);
  });

  // Live updates: connect to the integration-service SSE stream and pipe
  // product.changed events into the BOM table (with a CSS highlight) plus
  // the sidebar history panel.
  const historyPanel = document.getElementById('history-panel');
  const wsStatus = document.getElementById('ws-status');
  if (erpEventsUrl && historyPanel && wsStatus && erpBySku.size > 0) {
    const currencyBySku = new Map<string, string>();
    for (const [sku, p] of erpBySku) currencyBySku.set(sku, p.currency);
    startLiveUpdates({
      url: erpEventsUrl,
      currencyBySku,
      historyEl: historyPanel,
      statusEl: wsStatus,
      onChange: (ev) => {
        // Update the in-memory snapshot so re-renders pick up the new value.
        const existing = erpBySku.get(ev.sku);
        if (!existing) return;
        for (const c of ev.changes) {
          if (c.field === 'list_price' && typeof c.new === 'number') existing.listPrice = c.new;
          if (c.field === 'name' && typeof c.new === 'string') existing.name = c.new;
          if (c.field === 'qty_available' && typeof c.new === 'number') existing.onHand = c.new;
        }
        // Mutate the matching <tr>'s cells in place and run the fade-in.
        bomPanel.querySelectorAll<HTMLTableRowElement>(`tr[data-sku="${cssEscape(ev.sku)}"]`).forEach((tr) => {
          for (const c of ev.changes) {
            const cell = c.field === 'list_price'
              ? tr.querySelector<HTMLTableCellElement>('[data-cell="price"]')
              : c.field === 'name'
                ? tr.querySelector<HTMLTableCellElement>('[data-cell="name"]')
                : null;
            if (!cell) continue;
            if (c.field === 'list_price' && typeof c.new === 'number') {
              cell.textContent = `${c.new.toFixed(2)} ${existing.currency}`;
            } else if (c.field === 'name' && typeof c.new === 'string') {
              cell.textContent = c.new;
            }
            cell.classList.remove('cell-changed');
            // Force a reflow so the animation replays even if the class was just removed.
            void cell.offsetWidth;
            cell.classList.add('cell-changed');
          }
        });
      },
    });
  }

}

function findFirstRealNode(startId: string, nodes: Map<string, LoadedNode>): string | null {
  const node = nodes.get(startId);
  if (!node) return null;
  // Skip the OCCT outer wrapper (name starts with '<unnamed-') AND synthetic
  // mesh-wrapper nodes when locating the displayed root.
  const isWrapper = node.synthetic || node.name.startsWith('<');
  if (!isWrapper) return startId;
  for (const cid of node.childIds) {
    const found = findFirstRealNode(cid, nodes);
    if (found) return found;
  }
  return null;
}

function buildTreeItem(
  nodes: Map<string, LoadedNode>,
  id: string,
  nodeIdToRowIndexes: Map<string, number[]>,
): HTMLLIElement {
  const node = nodes.get(id);
  const li = document.createElement('li');
  li.dataset.idiagramId = id;
  const label = document.createElement('span');
  label.className = 'tree-label';
  label.textContent = node?.name ?? `<${id}>`;
  if (nodeIdToRowIndexes.has(id)) {
    const dot = document.createElement('span');
    dot.className = 'bom-dot';
    dot.title = 'Linked to BOM row(s)';
    label.appendChild(dot);
  }
  li.appendChild(label);

  if (node && node.childIds.length > 0) {
    // Recurse, skipping synthetic mesh wrappers.
    const realChildren = node.childIds.filter((cid) => {
      const c = nodes.get(cid);
      return c && !c.synthetic;
    });
    if (realChildren.length > 0) {
      const ul = document.createElement('ul');
      for (const cid of realChildren) {
        ul.appendChild(buildTreeItem(nodes, cid, nodeIdToRowIndexes));
      }
      li.appendChild(ul);
    }
  }
  return li;
}

function buildBomTable(
  bomLinks: BomLinksFile,
  erpBySku: Map<string, ErpProduct>,
  odooPublicUrl: string | null,
): HTMLElement {
  const showPrice = erpBySku.size > 0;

  const table = document.createElement('table');
  table.className = 'bom-table';
  const thead = document.createElement('thead');
  thead.innerHTML = showPrice
    ? '<tr><th>Part</th><th>Name</th><th>Qty</th><th>Price</th></tr>'
    : '<tr><th>Part</th><th>Name</th><th>Qty</th></tr>';
  table.appendChild(thead);
  const tbody = document.createElement('tbody');
  for (const r of bomLinks.rows) {
    const tr = document.createElement('tr');
    tr.dataset.rowIndex = String(r.index);
    tr.dataset.sku = r.partNumber;
    const erp = erpBySku.get(r.partNumber);
    if (erp) tr.classList.add('has-erp');
    const displayName = erp?.name ?? r.name;
    // When we have an Odoo product, link the SKU directly to its product page
    // on the public Odoo instance. The /odoo/inventory/products/<id> path is
    // Odoo 17+'s canonical URL.
    const partCell =
      erp && odooPublicUrl
        ? `<td><a href="${escapeAttr(odooPublicUrl)}/odoo/inventory/products/${erp.id}" target="_blank" rel="noopener" class="bom-part-link">${escapeHtml(r.partNumber)}</a></td>`
        : `<td>${escapeHtml(r.partNumber)}</td>`;
    const priceCell = showPrice
      ? `<td class="bom-price" data-cell="price">${erp ? `${erp.listPrice.toFixed(2)} ${escapeHtml(erp.currency)}` : ''}</td>`
      : '';
    tr.innerHTML = `${partCell}<td data-cell="name">${escapeHtml(displayName)}</td><td>${r.qty}</td>${priceCell}`;
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

function buildPartData(
  id: string,
  nodes: Map<string, LoadedNode>,
  nodeIdToRowIndexes: Map<string, number[]>,
  bomRowByIndex: Map<number, BomRow>,
  erpBySku: Map<string, ErpProduct>,
): PartData | null {
  const node = nodes.get(id);
  const rowIndexes = nodeIdToRowIndexes.get(id) ?? [];
  // Pick the first linked BOM row when a node has multiple (rare; happens
  // when several rows resolve to the same part). The active-card UX shows
  // one part at a time so we don't need to surface the multiplexing here.
  const row = rowIndexes.length > 0 ? bomRowByIndex.get(rowIndexes[0]!) : undefined;
  const erp = row ? erpBySku.get(row.partNumber) : undefined;
  if (!row && !erp && !node) return null;
  return {
    id,
    // Two-digit display number so "01" matches the designer's IKEA-style
    // formatting in the wireframes. Falls back to the node's own short id
    // when no BOM row is linked.
    n: row ? String(row.index).padStart(2, '0') : id.split('.').pop() ?? id,
    name: erp?.name ?? row?.name ?? node?.name ?? `node ${id}`,
    sku: row?.partNumber ?? id,
    price: erp?.listPrice ?? 0,
    currency: erp?.currency ?? '',
    stock: erp?.onHand ?? null,
  };
}

function escapeAttr(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;',
  );
}

// CSS.escape isn't available in every browser context the smoke tests run in;
// a tiny polyfill that handles the chars we actually care about (SKUs are
// short alphanumeric+dash strings).
function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/(["\\\]])/g, '\\$1');
}

main().catch((err) => {
  console.error(err);
});
